<script setup lang="ts">
import PasswordResetLayout from '@/components/auth/PasswordResetLayout.vue';
import { useBackendFetch } from '@/fetch';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const router = useRouter();
const email = ref('');
const isSubmitting = ref(false);
const sent = ref(false);
const requestError = ref('');
const errors = reactive({ email: '' });

const validEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function validateEmail() {
  errors.email = '';

  if (!email.value) {
    errors.email = t('emailRequired');
  } else if (!validEmailPattern.test(email.value)) {
    errors.email = t('emailInvalid');
  }

  return !errors.email;
}

async function submit() {
  requestError.value = '';

  if (!validateEmail()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const { error } = await useBackendFetch('/password/forgot')
      .post({ email: email.value })
      .json();

    if (error.value !== null) {
      requestError.value = t('PasswordReset.requestError');
      return;
    }

    sent.value = true;
  } catch {
    requestError.value = t('PasswordReset.requestError');
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <PasswordResetLayout>
    <header class="reset-header">
      <h1 class="reset-title">{{ $t('PasswordReset.forgotTitle') }}</h1>
      <p class="reset-description">
        {{ $t('PasswordReset.forgotDescription') }}
      </p>
    </header>

    <div v-if="sent" class="button-stack">
      <div class="status-box success" role="status">
        {{ $t('PasswordReset.emailSent') }}
      </div>
      <button
        type="button"
        class="primary-button"
        @click="router.replace('/login')"
      >
        {{ $t('PasswordReset.backToLogin') }}
      </button>
    </div>

    <form v-else class="reset-form" @submit.prevent="submit">
      <div class="form-group" :class="{ error: errors.email }">
        <label for="reset-email" class="form-label">{{
          $t('emailLabel')
        }}</label>
        <div class="input-wrapper">
          <svg
            class="input-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
            />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <input
            id="reset-email"
            v-model.trim="email"
            type="email"
            autocomplete="email"
            class="form-input"
            :placeholder="$t('LoginPage.emailPlaceholder')"
            @blur="validateEmail"
          />
        </div>
        <p v-if="errors.email" class="error-message">{{ errors.email }}</p>
      </div>

      <div v-if="requestError" class="status-box error" role="alert">
        {{ requestError }}
      </div>

      <div class="button-stack">
        <button type="submit" class="primary-button" :disabled="isSubmitting">
          <span v-if="!isSubmitting">{{ $t('PasswordReset.sendLink') }}</span>
          <span v-else class="spinner" aria-hidden="true"></span>
        </button>
        <button
          type="button"
          class="secondary-button"
          @click="router.replace('/login')"
        >
          {{ $t('PasswordReset.backToLogin') }}
        </button>
      </div>
    </form>
  </PasswordResetLayout>
</template>

<style scoped src="../assets/password-reset.css"></style>
